/**
 * SunoPersonasContext — глобальный список персон юзера + jobs для создания.
 *
 * Держит:
 *   - Список `personas[]` — загружен из `/music/personas`, обновляется
 *     при событии `suno-persona-created` / `suno-persona-deleted`
 *   - Persistent polling для create-job'а: `start(track)` запускает
 *     generatePersona → polling → после успеха refresh списка
 *
 * Доступно везде — Library (таб «Персоны»), виджет (picker в генерации),
 * SunoActionsSheet (кнопка «Персона» запускает start).
 */
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
	type ReactNode,
} from 'react'
import {
	generatePersona,
	getUserPersonas,
	renamePersona as apiRenamePersona,
	deletePersona as apiDeletePersona,
	type SunoPersona,
	type SunoTrack,
} from '../api/music'
import { waitForTask } from '../pages/generate/utils/waitForTask'
import { showToast } from '../components/Toast'
import { translateSunoError } from '../pages/generate/utils/sunoErrors'
import { useAuth } from './AuthContext'

const LS_PENDING_KEY = 'suno_personas_pending_v1'
const POLL_INTERVAL_MS = 10_000

interface PendingPersonaJob {
	trackId: string
	requestId: string
	title: string | null
	startedAt: number
}

interface SunoPersonasValue {
	personas: SunoPersona[]
	loading: boolean
	error: string | null
	refresh: () => Promise<void>
	/** Запустить создание персоны из трека. */
	start: (track: SunoTrack) => Promise<void>
	/** Идёт ли polling для этого трека. */
	isCreating: (trackId: string) => boolean
	/** Переименовать персону. */
	rename: (personaId: string, name: string) => Promise<void>
	/** Удалить персону (из нашей БД; на стороне Suno остаётся). */
	remove: (personaId: string) => Promise<void>
}

const SunoPersonasContext = createContext<SunoPersonasValue | null>(null)

function loadPending(): Record<string, PendingPersonaJob> {
	try {
		const raw = localStorage.getItem(LS_PENDING_KEY)
		if (!raw) return {}
		const parsed = JSON.parse(raw)
		if (!parsed || typeof parsed !== 'object') return {}
		const filtered: Record<string, PendingPersonaJob> = {}
		for (const [k, v] of Object.entries(parsed as Record<string, PendingPersonaJob>)) {
			if (v && typeof v === 'object' && v.requestId && v.startedAt) {
				// Запуск с номером задачи живёт, пока сервер не ответил (план «Ждём ответа нейросети», Ф3): прежние
				// 30 минут бросали следить за долгой задачей после перезагрузки.
				filtered[k] = v
			}
		}
		return filtered
	} catch {
		return {}
	}
}
function savePending(pending: Record<string, PendingPersonaJob>) {
	try {
		localStorage.setItem(LS_PENDING_KEY, JSON.stringify(pending))
	} catch {
		/* quota — запуск просто не восстановится после перезагрузки */
	}
}

export function SunoPersonasProvider({ children }: { children: ReactNode }) {
	const { user } = useAuth()
	const [personas, setPersonas] = useState<SunoPersona[]>([])
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [creatingIds, setCreatingIds] = useState<Set<string>>(
		() => new Set(Object.keys(loadPending())),
	)
	const activeRef = useRef<Set<string>>(new Set())
	const pendingRef = useRef<Record<string, PendingPersonaJob>>(loadPending())

	const refresh = useCallback(async () => {
		if (!user) {
			setPersonas([])
			return
		}
		setLoading(true)
		setError(null)
		try {
			const res = await getUserPersonas({ limit: 100 })
			setPersonas(res.personas || [])
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Не удалось загрузить персоны')
		} finally {
			setLoading(false)
		}
	}, [user])

	// Загрузить список при mount / login.
	useEffect(() => {
		refresh()
	}, [refresh])

	// Слушаем события создания/удаления — перезагружаем список.
	useEffect(() => {
		const handler = () => refresh()
		window.addEventListener('suno-persona-created', handler)
		window.addEventListener('suno-persona-deleted', handler)
		return () => {
			window.removeEventListener('suno-persona-created', handler)
			window.removeEventListener('suno-persona-deleted', handler)
		}
	}, [refresh])

	const markCreating = useCallback((trackId: string, running: boolean) => {
		setCreatingIds((prev) => {
			const next = new Set(prev)
			if (running) next.add(trackId)
			else next.delete(trackId)
			return next
		})
	}, [])

	const setPendingJob = useCallback((trackId: string, job: PendingPersonaJob | null) => {
		if (job) pendingRef.current[trackId] = job
		else delete pendingRef.current[trackId]
		savePending({ ...pendingRef.current })
	}, [])

	const runPolling = useCallback(
		async (trackId: string, requestId: string, trackTitle: string | null) => {
			if (activeRef.current.has(trackId)) return
			activeRef.current.add(trackId)
			markCreating(trackId, true)
			setPendingJob(trackId, {
				trackId,
				requestId,
				title: trackTitle,
				startedAt: pendingRef.current[trackId]?.startedAt ?? Date.now(),
			})

			try {
				// Ждём ответа сервера без потолка (waitForTask, план «Ждём ответа нейросети», Ф3).
				const s = await waitForTask(requestId, {
					minDelayMs: POLL_INTERVAL_MS,
					startedAt: pendingRef.current[trackId]?.startedAt,
				})
				if (s?.status === 'completed') {
					showToast(`Персона из «${trackTitle || 'трека'}» создана`, 'success')
					window.dispatchEvent(new CustomEvent('suno-persona-created', { detail: { trackId } }))
				} else if (s) {
					showToast(translateSunoError(s.error_message_ru || s.error_message, 'persona'), 'error')
				}
			} finally {
				activeRef.current.delete(trackId)
				markCreating(trackId, false)
				setPendingJob(trackId, null)
			}
		},
		[markCreating, setPendingJob],
	)

	const start = useCallback(
		async (track: SunoTrack) => {
			if (activeRef.current.has(track.id)) {
				showToast('Создание персоны уже идёт...', 'info')
				return
			}
			showToast(`Создаём персону из «${track.title?.trim() || 'трека'}»...`, 'info')
			const res = await generatePersona({ track_id: track.id })
			if (!res.ok || !res.request_id) {
				showToast(translateSunoError(res.error, 'persona'), 'error')
				return
			}
			runPolling(track.id, res.request_id, track.title)
		},
		[runPolling],
	)

	const isCreating = useCallback((trackId: string) => creatingIds.has(trackId), [creatingIds])

	const rename = useCallback(
		async (personaId: string, name: string) => {
			const res = await apiRenamePersona(personaId, name)
			if (!res.ok) {
				showToast(res.error || 'Не удалось переименовать', 'error')
				return
			}
			setPersonas((prev) => prev.map((p) => (p.id === personaId ? { ...p, name } : p)))
			showToast('Переименовано', 'success')
		},
		[],
	)

	const remove = useCallback(
		async (personaId: string) => {
			const res = await apiDeletePersona(personaId)
			if (!res.ok) {
				showToast(res.error || 'Не удалось удалить', 'error')
				return
			}
			setPersonas((prev) => prev.filter((p) => p.id !== personaId))
			showToast('Персона удалена', 'success')
			window.dispatchEvent(new CustomEvent('suno-persona-deleted', { detail: { personaId } }))
		},
		[],
	)

	// Restore pending jobs при старте.
	useEffect(() => {
		const pending = pendingRef.current
		const ids = Object.keys(pending)
		if (ids.length === 0) return
		const t = setTimeout(() => {
			for (const trackId of ids) {
				const job = pending[trackId]
				if (!job) continue
				runPolling(trackId, job.requestId, job.title)
			}
		}, 500)
		return () => clearTimeout(t)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const value = useMemo<SunoPersonasValue>(
		() => ({ personas, loading, error, refresh, start, isCreating, rename, remove }),
		[personas, loading, error, refresh, start, isCreating, rename, remove],
	)

	return (
		<SunoPersonasContext.Provider value={value}>{children}</SunoPersonasContext.Provider>
	)
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSunoPersonas(): SunoPersonasValue {
	const ctx = useContext(SunoPersonasContext)
	if (!ctx) {
		return {
			personas: [],
			loading: false,
			error: null,
			refresh: async () => {},
			start: async () => {},
			isCreating: () => false,
			rename: async () => {},
			remove: async () => {},
		}
	}
	return ctx
}
